import { useCallback, useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion, type Variants } from 'framer-motion'
import { RADIUS, SHELL, surfaceVariants } from '@workflow/ui'
import { useLocation, type Route } from '@/app/router'
import { useAppearance } from '@/features/appearance/appearance'
import { HomePage } from '@/features/home/HomePage'
import { SettingsPage } from '@/features/settings/SettingsPage'
import { ClientsPage } from '@/features/clients/ClientsPage'
import { ClientDetailPage } from '@/features/clients/ClientDetailPage'
import { JobsPage } from '@/features/jobs/JobsPage'
import { JobDetailPage } from '@/features/jobs/JobDetailPage'
import { PaymentSheetsPage } from '@/features/payments/PaymentSheetsPage'
import { PaymentSheetPage } from '@/features/payments/PaymentSheetPage'
import { StatementsPage } from '@/features/payments/StatementsPage'
import { PeoplePage } from '@/features/people/PeoplePage'
import { ActivityPage } from '@/features/activity/ActivityPage'
import { AnnouncementsPage } from '@/features/announcements/AnnouncementsPage'
import { PlayerHomePage, PlayerWorkPage } from '@/features/portal/PlayerPages'
import { useSession } from '@/lib/api/hooks'
import { NotFoundPage } from './NotFoundPage'
import { LegalPage } from './LegalPages'
import { Sidebar } from './Sidebar'
import { TopStrip } from './TopStrip'
import { useCompactLayout } from './useCompactLayout'

const OPEN_KEY = 'ops.sidebar.open'

const reducedPageVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.12 } },
  exit: { opacity: 0, transition: { duration: 0.12 } },
}

function loadOpen(): boolean {
  try {
    return window.localStorage.getItem(OPEN_KEY) !== '0'
  } catch {
    return true
  }
}

/** The player's pages: their four destinations and their own settings; anything else is not theirs. */
function PlayerPage({ route }: { route: Route }) {
  switch (route.name) {
    case 'home':
      return <PlayerHomePage />
    case 'work':
      return <PlayerWorkPage />
    case 'payments':
      return <StatementsPage />
    case 'announcements':
      return <AnnouncementsPage id={route.id} />
    case 'settings':
      return <SettingsPage section={route.section} />
    case 'privacy':
    case 'terms':
    case 'cookies':
      return <LegalPage name={route.name} />
    default:
      return <NotFoundPage />
  }
}

function Page({ route }: { route: Route }) {
  switch (route.name) {
    case 'home':
      return <HomePage />
    case 'jobs':
      return <JobsPage />
    case 'job':
      return <JobDetailPage id={route.id} />
    case 'clients':
      return <ClientsPage />
    case 'client':
      return <ClientDetailPage id={route.id} />
    case 'people':
      return <PeoplePage section={route.section} />
    case 'activity':
      return <ActivityPage />
    case 'announcements':
      return <AnnouncementsPage id={route.id} />
    case 'payments':
      return <PaymentSheetsPage />
    case 'payment-sheet':
      return <PaymentSheetPage id={route.id} />
    case 'work':
      return <NotFoundPage />
    case 'settings':
      return <SettingsPage section={route.section} />
    case 'privacy':
    case 'terms':
    case 'cookies':
      return <LegalPage name={route.name} />
    case 'not-found':
      return <NotFoundPage />
  }
}

/**
 * The shell.
 *
 * ---- One ground, one panel -------------------------------------------------
 *
 * The window's ground is the sunken surface, and the sidebar stands on it
 * flush to every edge — the ground, not an object on it. The page is the
 * object: a surface panel that rounds its left corners against the column
 * beside it and squares off when the column is gone. `overflow-hidden` is
 * what makes the corner real: without it a scrolling page paints square
 * right through the rounded edge.
 *
 * ---- The sidebar is the console's mechanism ---------------------------------
 *
 * The `aside` eases its width while the column inside keeps a fixed width
 * of its own, so the motion only reveals a finished layout instead of
 * reflowing one. Open, or gone — there is no icon rail; the toggle lives in
 * the strip over the page, and Ctrl B brings the column back. Below the
 * compact breakpoint the column leaves the flow entirely and slides over the
 * page under a scrim.
 *
 * ---- One shell, two rooms ---------------------------------------------------
 *
 * A player gets this same shell — column, strip, page frame, settings — with
 * their own destinations in the column and their own pages behind them. The
 * bell is the same bell, reading the same feed, for everyone.
 *
 * ---- Surfaces stack, they do not queue -------------------------------------
 *
 * Both pages stay mounted during navigation. The arriving page reveals over
 * the outgoing page, which settles beneath it. Each page is its own scroll
 * container, so the old page remains where it was during the handoff.
 */
export function AppShell() {
  const { route, key } = useLocation()
  const compact = useCompactLayout()
  const [open, setOpen] = useState(loadOpen)
  const appearance = useAppearance()
  const reducedMotion = useReducedMotion()
  const isPlayer = useSession().data?.account?.role === 'player'

  useEffect(() => {
    try {
      window.localStorage.setItem(OPEN_KEY, open ? '1' : '0')
    } catch {
      // Storage blocked. The choice lasts for this session only.
    }
  }, [open])

  const toggleSidebar = useCallback(() => setOpen((value) => !value), [])

  // Ctrl B, as in the console: the column's second way back, so closing it
  // never strands the keyboard. Escape joins it when the column floats over
  // the page — the drawer is an overlay there, and overlays answer Escape.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'b') {
        event.preventDefault()
        toggleSidebar()
        return
      }
      if (event.key === 'Escape' && compact && open) {
        event.preventDefault()
        toggleSidebar()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [toggleSidebar, compact, open])

  /** The page only rounds when there is a sidebar beside it to rest on. */
  const rounded = open && !compact

  return (
    // The shell's ground is the sidebar's gray, not the window's: the only
    // place it ever shows is the quarter-discs the page's rounded corners
    // cut out, so the gray wraps the curve instead of ending in a straight
    // line beside it. In light the two grounds are already one colour.
    <div className="relative flex h-dvh overflow-hidden bg-sidebar">
      <Sidebar
        open={open}
        compact={compact}
        onToggle={toggleSidebar}
        // On a narrow window a followed row closes the drawer: the page move
        // is already under way and the drawer would sit over the destination.
        onNavigate={compact ? toggleSidebar : undefined}
      />

      <div
        className="relative z-10 min-w-0 flex-1 overflow-hidden bg-page"
        style={{
          borderTopLeftRadius: rounded ? RADIUS.xl : 0,
          borderBottomLeftRadius: rounded ? RADIUS.xl : 0,
          transition: 'border-radius 420ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {/* The ambient pools drift over the page panel, clipped by its own
            rounded corners — unless the room asked for them gone. The ground
            stays still; the working surface is the thing that reads as live. */}
        {appearance.ambient ? <div className="wf-ambient absolute" aria-hidden /> : null}

        {/* The veil over the strip itself: solid through the strip's own band,
            then a short fade below it. Scrolled content dims as it approaches
            the top and is gone before it reaches the toggle. */}
        <div
          className="pointer-events-none absolute inset-x-0 z-20"
          style={{
            height: SHELL.topStripHeight + 28,
            backgroundImage: `linear-gradient(to bottom, var(--page) ${SHELL.topStripHeight}px, transparent ${SHELL.topStripHeight + 28}px)`,
          }}
        />

        <TopStrip compact={compact} sidebarOpen={open} onToggle={toggleSidebar} />

        <AnimatePresence initial={false}>
          <motion.main
            key={key}
            variants={reducedMotion ? reducedPageVariants : surfaceVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="absolute inset-0 overflow-y-auto"
          >
            {isPlayer ? <PlayerPage route={route} /> : <Page route={route} />}
          </motion.main>
        </AnimatePresence>

        {/* The clock keeps to the main page and leaves with an animation,
            never a cut: presence here outlives the page, so the exit plays
            while the next surface arrives. */}

      </div>
    </div>
  )
}
